import { useCallback, useEffect, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useResource, useQueryParam } from 'react-resource-router';

import { Box, Flex } from '@atlaskit/primitives';

import { PAGE_QUERY_PARAM } from 'src/components/pipelines/constants';
import {
  getCapabilities,
  getRepository,
} from 'src/components/pipelines/redux/selectors/pipelines';
import { getPages } from 'src/components/pipelines/utils/pagination';
import { useAnalytics } from 'src/components/settings/analytics';
import { useParams } from 'src/router/hooks';

import {
  HowToUseMergeQueuesLink,
  InlineSettings,
  MergeQueueDetails,
  MergeQueueInactive,
  MergeQueuePicker,
  MergeQueuePullRequestsTable,
  MergeQueuesEmpty,
  MergeQueuesLoading,
  MergeQueuesPipelinesDisabled,
  MergeQueuesUnavailable,
} from './components';
import { MERGE_QUEUE_ACTION_EVENT_MAP } from './constants';
import {
  useCreateEditMergeQueue,
  useDeleteMergeQueue,
  useMergeQueuePipelineDefinitions,
} from './hooks';
import { messages } from './merge-queues.i18n';
import { MergeQueueActionModal, MergeQueueCreateEditModal } from './modals';
import {
  useFocusedMergeQueue,
  useIsEmpty,
  useMergeQueuesActions,
} from './provider';
import { mergeQueuesRouteResource } from './resources';
import {
  MergeQueueActionModalType,
  MergeQueueState,
  MergeQueuesProps,
} from './types';
import { MERGE_QUEUES_PAGE_LENGTH } from './utils';

export const MergeQueuesSettings = ({
  mergeQueues,
  mergeQueuesLoading,
}: MergeQueuesProps) => {
  const { publishButtonClickEvent, publishScreenEvent } = useAnalytics({
    source: 'MergeQueuesSettingsPage',
  });
  const { repositoryOwner, repositorySlug, mergeQueueUuid } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    mergeQueueUuid?: string;
  }>();

  const [{ focusedMergeQueue }] = useFocusedMergeQueue();
  const repository = useSelector(getRepository);
  const {
    loading: mergeQueuePipelineDefinitionsLoading,
    isPipelineDefinedForMergeQueue,
  } = useMergeQueuePipelineDefinitions(
    repository,
    focusedMergeQueue ?? undefined
  );
  const [{ isEmpty }] = useIsEmpty();
  const [
    ,
    {
      setFocusedMergeQueue,
      setIsCreateModalOpen,
      setIsEditModalOpen,
      setIsEmpty,
      setIsLoading,
      setMergeQueueActionModal,
      setMergeQueues,
      setMergeQueuesPages,
    },
  ] = useMergeQueuesActions();

  const { refresh: refreshMergeQueues } = useResource(mergeQueuesRouteResource);

  const { editMergeQueue } = useCreateEditMergeQueue({
    owner: repositoryOwner,
    slug: repositorySlug,
    onClose: () => {},
    onSuccess: refreshMergeQueues,
  });

  const [page, setPageParam] = useQueryParam(PAGE_QUERY_PARAM);

  const navigateToPreviousPage = useCallback(() => {
    if (!page) {
      refreshMergeQueues();
      return;
    }

    const currentPage = Number(page);
    if (currentPage > 1) {
      setPageParam(String(currentPage - 1));
    } else if (currentPage === 1) {
      setPageParam(undefined);
    }
  }, [page, refreshMergeQueues, setPageParam]);

  const { deleteMergeQueue } = useDeleteMergeQueue({
    mergeQueueUuid: focusedMergeQueue?.uuid || '',
    owner: repositoryOwner,
    slug: repositorySlug,
    onClose: () => {},
    onSuccess:
      mergeQueues?.values?.length === 1
        ? navigateToPreviousPage
        : refreshMergeQueues,
  });

  const capabilities = useSelector(getCapabilities);

  useEffect(function publishMergeQueuesSettingsScreen() {
    publishScreenEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function setMergeQueuesState() {
      setIsLoading(mergeQueuesLoading);

      if (!mergeQueuesLoading) {
        setMergeQueues(mergeQueues?.values ?? []);
        setMergeQueuesPages(
          getPages(mergeQueues?.size ?? 0, MERGE_QUEUES_PAGE_LENGTH)
        );
        setIsEmpty(mergeQueues?.values?.length === 0);
        if (mergeQueueUuid) {
          setFocusedMergeQueue(
            mergeQueues?.values.find(mq => mq.uuid === mergeQueueUuid)
          );
        } else {
          setFocusedMergeQueue(undefined);
        }
      }
    },
    [
      mergeQueueUuid,
      mergeQueuesLoading,
      mergeQueues,
      setFocusedMergeQueue,
      setIsEmpty,
      setIsLoading,
      setMergeQueues,
      setMergeQueuesPages,
    ]
  );

  const isPaidOrPremium =
    capabilities.hasFetchedCapabilities && capabilities.isPaidOrPremium;
  const isPipelinesEnabled =
    capabilities.hasFetchedCapabilities && capabilities.pipelinesEnabled;

  const getContent = useMemo(() => {
    if (
      !capabilities.hasFetchedCapabilities ||
      mergeQueuesLoading ||
      mergeQueuePipelineDefinitionsLoading
    ) {
      return <MergeQueuesLoading />;
    }
    if (!isPaidOrPremium) {
      return <MergeQueuesUnavailable />;
    }
    if (!isPipelinesEnabled) {
      return <MergeQueuesPipelinesDisabled />;
    }
    if (isEmpty) {
      return <MergeQueuesEmpty isRenderedInSettings />;
    }
    return (
      <>
        <Box paddingBlockStart="space.100" paddingBlockEnd="space.300">
          <Flex
            gap="space.150"
            alignItems="center"
            justifyContent="space-between"
          >
            <MergeQueuePicker isRenderedInSettings />
            {focusedMergeQueue && (
              <InlineSettings
                mergeQueue={focusedMergeQueue}
                pullRequestsCount={focusedMergeQueue.pullRequestsCount}
                onClearClick={() => {
                  publishButtonClickEvent('clearMergeQueue');
                  setMergeQueueActionModal(MergeQueueActionModalType.CLEAR);
                }}
                onDeleteClick={() => {
                  publishButtonClickEvent('deleteMergeQueue');
                  if (focusedMergeQueue.uuid) {
                    deleteMergeQueue(focusedMergeQueue.uuid);
                  }
                }}
                onEditClick={() => {
                  publishButtonClickEvent('editMergeQueue');
                  setIsCreateModalOpen(false);
                  setIsEditModalOpen(true);
                }}
                onStateUpdate={(state: MergeQueueState) => {
                  publishButtonClickEvent(MERGE_QUEUE_ACTION_EVENT_MAP[state]);
                  editMergeQueue({
                    state,
                    mergeQueueUuid: focusedMergeQueue.uuid,
                  });
                }}
              />
            )}
          </Flex>
        </Box>
        {focusedMergeQueue && (
          <>
            <MergeQueueDetails />
            {focusedMergeQueue.state === MergeQueueState.INACTIVE &&
            !isPipelineDefinedForMergeQueue ? (
              <MergeQueueInactive />
            ) : (
              <MergeQueuePullRequestsTable isRenderedInSettings />
            )}
          </>
        )}
      </>
    );
  }, [
    capabilities,
    mergeQueuesLoading,
    isPaidOrPremium,
    isPipelinesEnabled,
    isEmpty,
    focusedMergeQueue,
    mergeQueuePipelineDefinitionsLoading,
    isPipelineDefinedForMergeQueue,
    publishButtonClickEvent,
    setMergeQueueActionModal,
    setIsCreateModalOpen,
    setIsEditModalOpen,
    editMergeQueue,
    deleteMergeQueue,
  ]);

  return (
    <>
      <FormattedMessage
        {...messages.introMsg}
        tagName="span"
        values={{
          learnMore: <HowToUseMergeQueuesLink />,
        }}
      />
      {getContent}
      <MergeQueueActionModal />
      <MergeQueueCreateEditModal />
    </>
  );
};
